import { useEffect, useState } from 'react';
import { useAttachmentUrl } from './use-attachment-url';

interface UseAttachmentObjectUrlParams {
  caseId: string;
  sourceItemId: string | undefined;
  enabled: boolean;
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
  );
}

/**
 * Henter vedlegget som blob og lager en object-URL med korrekt content-type.
 * Nødvendig fordi Storage-emulatorens media-URL serverer lagret content-type
 * (ofte application/octet-stream) og ignorerer responseType/responseDisposition,
 * noe som ellers tvinger nedlasting i stedet for inline-visning (f.eks. PDF).
 */
export function useAttachmentObjectUrl({
  caseId,
  sourceItemId,
  enabled,
}: UseAttachmentObjectUrlParams) {
  const { url, contentType, isError, isLoading } = useAttachmentUrl({
    caseId,
    sourceItemId,
    enabled,
    disposition: 'inline',
  });
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);

  useEffect(() => {
    if (!url) {
      setObjectUrl(null);
      return;
    }
    const abortController = new AbortController();
    let createdUrl: string | null = null;
    setFetching(true);
    setFetchError(false);
    void fetch(url, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Kunne ikke hente vedlegg.');
        }
        return response.blob();
      })
      .then((blob) => {
        const typed = contentType ? new Blob([blob], { type: contentType }) : blob;
        createdUrl = URL.createObjectURL(typed);
        setObjectUrl(createdUrl);
      })
      .catch((error: unknown) => {
        if (!isAbortError(error)) {
          setFetchError(true);
        }
      })
      .finally(() => setFetching(false));
    return () => {
      abortController.abort();
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [url, contentType]);

  return {
    objectUrl,
    isError: isError || fetchError,
    isLoading: isLoading || fetching,
  };
}
